import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Icon } from '@/components/ui';
import { useCustomerQuery, useEstablishmentsQuery } from '@/features/organization/queries';
import { formatTaxId } from '@/lib/format';
import { PHONE_QUERY, useMediaQuery } from '@/lib/hooks';
import {
  THEME_LABELS,
  THEME_PREFERENCES,
  useThemePreference,
  type ThemePreference,
} from '@/lib/theme';

import type { StoreSummary } from './useStoreSummary';
import styles from './StoreFooter.module.css';

interface FooterLink {
  readonly label: string;
  readonly to?: string | undefined;
  readonly onClick?: (() => void) | undefined;
}

export interface StoreFooterProps {
  readonly summary: StoreSummary;
  readonly onHelp: () => void;
}

/**
 * The storefront footer (Figma "Esboço/Rodapé", D1 and M1): "Voltar ao topo", the site map of the
 * buying journey, which company and unit the session buys for, the appearance and the version.
 *
 * Only pages that exist are linked. The Figma also draws a help centre, help articles, "Sobre",
 * "Status do sistema", the customer-service phone and e-mail, the DPO and the legal documents;
 * none of those exist yet (Figma 73:3369, question 7), so they are left out rather than linked to
 * nowhere — the PR lists them.
 *
 * On a phone the columns fold into one disclosure each (M1).
 */
export function StoreFooter({ summary, onHelp }: StoreFooterProps) {
  const customer = useCustomerQuery();
  const establishments = useEstablishmentsQuery();
  const [theme, setTheme] = useThemePreference();
  const [lifting, setLifting] = useState(0);
  // Columns are open on a desktop and fold to one disclosure each on a phone (M1).
  const phone = useMediaQuery(PHONE_QUERY);
  const current = establishments.data?.find((unit) => unit.name === summary.establishmentName);

  const columns: { title: string; links: FooterLink[] }[] = [
    {
      title: 'Comprar',
      links: [
        { label: 'Início', to: '/' },
        { label: 'Todas as categorias', to: '/catalogo' },
        { label: 'Comprar de novo', to: '/#comprar-de-novo' },
        { label: 'Mais comprados', to: '/#mais-comprados' },
        { label: 'Lista rápida', to: '/catalogo' },
        { label: 'Carrinho', to: '/carrinho' },
      ],
    },
    {
      title: 'Pedidos e pagamentos',
      links: [
        { label: 'Meus pedidos', to: '/pedidos' },
        { label: 'Histórico de compras', to: '/pedidos/historico' },
        ...(summary.paysBoletos ? [{ label: 'Boletos', to: '/pagamentos' }] : []),
        { label: 'Entregas', to: '/rastreamento' },
        { label: 'Contrato e prazo de pagamento', to: '/contratos' },
      ],
    },
    {
      title: 'Ajuda',
      links: [{ label: 'Atalhos de teclado (⌘K)', onClick: onHelp }],
    },
    {
      title: 'Instoque',
      links: [{ label: 'Minha organização', to: '/minha-organizacao' }],
    },
  ];

  const address = [current?.address_line, current?.city, current?.state]
    .filter(Boolean)
    .join(' · ');

  return (
    <footer className={styles.footer}>
      <button
        type="button"
        className={styles.top}
        data-lift={lifting === 0 ? undefined : lifting}
        onClick={() => {
          setLifting((value) => (value === 1 ? 2 : 1));
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      >
        <Icon name="chevronUp" size={16} className={styles.topIcon} />
        Voltar ao topo
      </button>

      <div className={styles.columns}>
        {columns.map((column) => (
          <details key={`${column.title}-${String(phone)}`} className={styles.column} open={!phone}>
            <summary className={styles.columnTitle}>
              {column.title}
              <Icon name="chevronDown" size={16} className={styles.columnChevron} />
            </summary>
            <ul className={styles.links}>
              {column.links.map((link) => (
                <li key={link.label}>
                  {link.to !== undefined ? (
                    <Link to={link.to} className={styles.link}>
                      {link.label}
                    </Link>
                  ) : (
                    <button type="button" className={styles.link} onClick={link.onClick}>
                      {link.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </details>
        ))}

        {summary.establishmentName !== undefined ? (
          <div className={styles.buyingFor}>
            <p className={styles.eyebrow}>Você está comprando para</p>
            <p className={styles.buyingUnit}>
              {summary.establishmentName}
              {summary.establishmentCount > 1
                ? ` · 1 de ${String(summary.establishmentCount)} unidades`
                : null}
            </p>
            {customer.data !== undefined ? (
              <p className={styles.buyingCompany}>
                {customer.data.legal_name} · CNPJ {formatTaxId(customer.data.tax_id)}
              </p>
            ) : null}
            {address !== '' ? <p className={styles.buyingCompany}>{address}</p> : null}
          </div>
        ) : null}
      </div>

      <div className={styles.base}>
        <p className={styles.copy}>
          <span className={styles.mark} aria-hidden="true">
            In
          </span>
          © {new Date().getFullYear()} Instoque · compras de food service com a Sapore
        </p>
        <label className={styles.theme}>
          <span className="visually-hidden">Aparência</span>
          <select
            className={styles.themeSelect}
            value={theme}
            onChange={(event) => {
              setTheme(event.target.value as ThemePreference);
            }}
          >
            {THEME_PREFERENCES.map((option) => (
              <option key={option} value={option}>
                Aparência: {THEME_LABELS[option]}
              </option>
            ))}
          </select>
          <Icon name="chevronDown" size={14} className={styles.themeChevron} />
        </label>
        <p className={styles.version}>
          versão <code>{import.meta.env.VITE_APP_VERSION}</code>
        </p>
      </div>
    </footer>
  );
}
